/**
 * Admin → Company Research → Dictionaries — typed api-client calls + TanStack
 * Query hooks (E07-F03-T03 / T04, web side).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). Transport uses the
 * shared `apiClient` (auto-injects the session Bearer token, normalises the
 * response, never throws) + `unwrap` (the single throw-at-the-Query-boundary
 * helper). Contracts come from `@eer/shared` (research-dictionaries.ts) so the SPA
 * speaks the exact same shapes the Fastify route validates.
 *
 * Endpoints (1:1 with the two designed Company-Research commands — see
 * routes/research-dictionaries.ts; architecture.md §7):
 *   GET  /research/dictionaries   research_get_dictionaries → useResearchDictionaries()
 *   POST /research/dictionaries   research_save_dictionary  → useSaveDictionary()
 *
 * The save body is the discriminated add | remove | reorder union. Both the
 * Settings card (T03) and the Intake autocomplete datalists (T04) read the SAME
 * query (`RESEARCH_DICTIONARIES_QUERY_KEY`), so a term added in Settings appears
 * in the Intake suggestions after the cache is seeded with the fresh snapshot the
 * write returns (no second round-trip) — that is exactly T04 acceptance #3.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  getDictionariesResponseSchema,
  getUnmatchedSubcategoriesResponseSchema,
  saveDictionaryResponseSchema,
  type DictionariesSnapshot,
  type SaveDictionaryInput,
  type UnmatchedSubcategory,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

/** Feature-local query key (kept out of the shared registry). */
export const RESEARCH_DICTIONARIES_QUERY_KEY = ['research', 'dictionaries'] as const;

/** GET /research/dictionaries — active snapshot of all three keys, ordered. */
async function fetchDictionaries(): Promise<DictionariesSnapshot> {
  const result = await apiClient.get<unknown>('/research/dictionaries', {
    command: 'research_get_dictionaries',
  });
  return getDictionariesResponseSchema.parse(unwrap(result)).dictionaries;
}

/** POST /research/dictionaries — add | remove | reorder; returns fresh snapshot. */
async function postSaveDictionary(input: SaveDictionaryInput): Promise<DictionariesSnapshot> {
  const result = await apiClient.post<unknown>('/research/dictionaries', input, {
    command: 'research_save_dictionary',
  });
  return saveDictionaryResponseSchema.parse(unwrap(result)).dictionaries;
}

/**
 * The active dictionary snapshot. Shared by the Settings card (edit) and the
 * Intake form (read-only autocomplete sources) — both subscribe to the same key,
 * so a write in Settings re-renders the Intake datalists too.
 */
export function useResearchDictionaries(): UseQueryResult<DictionariesSnapshot, ApiResultError> {
  return useQuery<DictionariesSnapshot, ApiResultError>({
    queryKey: RESEARCH_DICTIONARIES_QUERY_KEY,
    queryFn: fetchDictionaries,
  });
}

/** E07-F02-T14 — query key of the unrecognized sub-categories list. */
export const UNMATCHED_SUBCATEGORIES_QUERY_KEY = ['research', 'dictionaries', 'unmatched'] as const;

/** GET /research/dictionaries/unmatched — sub-categories no industry-map rule recognizes. */
async function fetchUnmatchedSubcategories(): Promise<UnmatchedSubcategory[]> {
  const result = await apiClient.get<unknown>('/research/dictionaries/unmatched', {
    command: 'research_get_unmatched_subcategories',
  });
  return getUnmatchedSubcategoriesResponseSchema.parse(unwrap(result)).items;
}

/** The unrecognized sub-categories (admin Dictionaries card). */
export function useUnmatchedSubcategories(): UseQueryResult<
  UnmatchedSubcategory[],
  ApiResultError
> {
  return useQuery<UnmatchedSubcategory[], ApiResultError>({
    queryKey: UNMATCHED_SUBCATEGORIES_QUERY_KEY,
    queryFn: fetchUnmatchedSubcategories,
  });
}

/**
 * Save mutation (add / remove / reorder). Seeds the cache with the authoritative
 * snapshot the write returns (avoids a flash) and invalidates so any other
 * subscriber (the Intake datalists) re-renders with the new terms.
 */
export function useSaveDictionary(): UseMutationResult<
  DictionariesSnapshot,
  ApiResultError,
  SaveDictionaryInput
> {
  const queryClient = useQueryClient();
  return useMutation<DictionariesSnapshot, ApiResultError, SaveDictionaryInput>({
    mutationFn: postSaveDictionary,
    onSuccess: (snapshot) => {
      queryClient.setQueryData(RESEARCH_DICTIONARIES_QUERY_KEY, snapshot);
      void queryClient.invalidateQueries({ queryKey: RESEARCH_DICTIONARIES_QUERY_KEY });
    },
  });
}
